import React, { useState, useEffect, useCallback } from 'react';
import { REQUIREMENTS_EXPORT_COLUMNS, runRequirementsExport } from './requirementsExport';

const FORMATS = [
  { key: 'csv',   label: 'CSV',   desc: 'Spreadsheet-ready, universal',   icon: 'csv',  disabled: false },
  { key: 'excel', label: 'Excel', desc: 'Formatted workbook (.xlsx)',       icon: 'xlsx', disabled: false },
  { key: 'html',  label: 'HTML',  desc: 'Branded report, print-ready',     icon: 'html', disabled: false },
  { key: 'pdf',   label: 'PDF',   desc: 'Coming soon',                     icon: 'pdf',  disabled: true  },
];

export default function RequirementsExportModal({ isOpen, onClose, data, projectName }) {
  const [selectedFormat, setSelectedFormat] = useState('csv');
  const [checkedCols, setCheckedCols] = useState(() =>
    new Set(REQUIREMENTS_EXPORT_COLUMNS.map(c => c.key))
  );
  const [includeHeader, setIncludeHeader] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setSelectedFormat('csv');
      setCheckedCols(new Set(REQUIREMENTS_EXPORT_COLUMNS.map(c => c.key)));
      setIncludeHeader(true);
    }
  }, [isOpen]);

  const toggleCol = useCallback((key) => {
    setCheckedCols(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  const hasAnyCol = REQUIREMENTS_EXPORT_COLUMNS.some(c => checkedCols.has(c.key));
  const count = (data || []).length;

  function handleExport() {
    runRequirementsExport({
      format: selectedFormat,
      data,
      columns: REQUIREMENTS_EXPORT_COLUMNS.filter(c => checkedCols.has(c.key)).map(c => c.key),
      projectName,
      includeHeader,
    });
    onClose();
  }

  if (!isOpen) return null;

  return (
    <div
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', backdropFilter:'blur(2px)', zIndex:9000, display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background:'#fff', borderRadius:12, boxShadow:'0 8px 40px rgba(0,0,0,0.18)', width:520, maxWidth:'calc(100vw - 2rem)', maxHeight:'calc(100vh - 4rem)', display:'flex', flexDirection:'column', overflow:'hidden', fontFamily:"'Poppins', sans-serif" }}>

        {/* Header */}
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', padding:'1.375rem 1.5rem 1.125rem', borderBottom:'1px solid #f0f0f0' }}>
          <div>
            <div style={{ fontSize:'1rem', fontWeight:600, color:'#333' }}>Export Requirements</div>
            {projectName && <div style={{ fontSize:'0.78rem', color:'#999', marginTop:2 }}>{projectName}</div>}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', width:30, height:30, borderRadius:6, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#999' }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M1 1l12 12M13 1L1 13"/></svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:'auto', padding:'1.25rem 1.5rem', display:'flex', flexDirection:'column', gap:'1.375rem' }}>

          {/* Format */}
          <div>
            <div style={{ fontSize:'0.72rem', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.8px', color:'#000', marginBottom:'0.625rem' }}>Export format</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'0.625rem' }}>
              {FORMATS.map(f => (
                <div
                  key={f.key}
                  onClick={() => !f.disabled && setSelectedFormat(f.key)}
                  style={{
                    display:'flex', flexDirection:'column', alignItems:'center', gap:'0.5rem',
                    padding:'0.875rem 0.5rem 0.75rem', border:`1.5px solid ${!f.disabled && selectedFormat === f.key ? '#e97024' : '#e0e0e0'}`,
                    borderRadius:8, cursor: f.disabled ? 'default' : 'pointer', position:'relative',
                    background: !f.disabled && selectedFormat === f.key ? 'rgba(233,112,36,0.04)' : '#fff',
                    boxShadow: !f.disabled && selectedFormat === f.key ? '0 0 0 3px rgba(233,112,36,0.12)' : 'none',
                    opacity: f.disabled ? 0.6 : 1,
                  }}
                >
                  {!f.disabled && selectedFormat === f.key && (
                    <div style={{ position:'absolute', top:'0.4rem', right:'0.4rem', width:16, height:16, background:'#e97024', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <svg width="8" height="8" viewBox="0 0 9 9" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1.5 4.5l2 2 4-4"/></svg>
                    </div>
                  )}
                  <FormatIcon type={f.icon} />
                  <div style={{ fontSize:'0.75rem', fontWeight:600, color:'#333' }}>{f.label}</div>
                  <div style={{ fontSize:'0.67rem', color:'#999', textAlign:'center', lineHeight:1.3 }}>{f.desc}</div>
                  {f.disabled && <div style={{ fontSize:'0.58rem', fontWeight:600, background:'#f0f0f0', color:'#999', borderRadius:3, padding:'0.1rem 0.3rem', letterSpacing:'0.3px', textTransform:'uppercase' }}>Soon</div>}
                </div>
              ))}
            </div>
          </div>

          <div style={{ height:1, background:'#f0f0f0' }} />

          {/* Columns */}
          <div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.625rem' }}>
              <div style={{ fontSize:'0.72rem', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.8px', color:'#000' }}>Columns to include</div>
              <div style={{ display:'flex', gap:'0.5rem', alignItems:'center' }}>
                <button onClick={() => setCheckedCols(new Set(REQUIREMENTS_EXPORT_COLUMNS.map(c => c.key)))} style={{ fontSize:'0.72rem', fontWeight:500, color:'#e97024', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit' }}>Select all</button>
                <span style={{ fontSize:'0.72rem', color:'#e0e0e0' }}>·</span>
                <button onClick={() => setCheckedCols(new Set(REQUIREMENTS_EXPORT_COLUMNS.filter(c => c.locked).map(c => c.key)))} style={{ fontSize:'0.72rem', fontWeight:500, color:'#e97024', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit' }}>Deselect all</button>
              </div>
            </div>
            <div style={{ border:'1px solid #f0f0f0', borderRadius:8, overflow:'hidden' }}>
              {REQUIREMENTS_EXPORT_COLUMNS.map((col, i) => (
                <div
                  key={col.key}
                  onClick={() => !col.locked && toggleCol(col.key)}
                  style={{ display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.625rem 0.875rem', borderBottom: i < REQUIREMENTS_EXPORT_COLUMNS.length - 1 ? '1px solid #f0f0f0' : 'none', cursor: col.locked ? 'default' : 'pointer', background:'#fff' }}
                >
                  <div style={{ width:16, height:16, borderRadius:4, border:`1.5px solid ${col.locked ? '#d0d0d0' : checkedCols.has(col.key) ? '#e97024' : '#d0d0d0'}`, background: col.locked ? '#f0f0f0' : checkedCols.has(col.key) ? '#e97024' : '#fff', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {(col.locked || checkedCols.has(col.key)) && (
                      <svg width="9" height="9" viewBox="0 0 9 9" fill="none" stroke={col.locked ? '#bbb' : 'white'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1.5 4.5l2 2 4-4"/></svg>
                    )}
                  </div>
                  <span style={{ fontSize:'0.82rem', color:'#333', fontWeight:500, flex:1 }}>{col.label}</span>
                  {col.locked && <span style={{ fontSize:'0.62rem', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.4px', color:'#bbb', background:'#f5f5f5', borderRadius:3, padding:'0.1rem 0.35rem' }}>Required</span>}
                </div>
              ))}
            </div>
          </div>

          <div style={{ height:1, background:'#f0f0f0' }} />

          {/* Options */}
          <div>
            <div style={{ fontSize:'0.72rem', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.8px', color:'#000', marginBottom:'0.625rem' }}>Options</div>
            <div style={{ border:'1px solid #f0f0f0', borderRadius:8, overflow:'hidden' }}>
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'1rem', padding:'0.75rem 0.875rem' }}>
                <div>
                  <div style={{ fontSize:'0.82rem', fontWeight:500, color:'#333' }}>Project header</div>
                  <div style={{ fontSize:'0.72rem', color:'#999', marginTop:2, lineHeight:1.4 }}>Include project name, export date, and active filters at the top of the export.</div>
                </div>
                <Toggle checked={includeHeader} onChange={setIncludeHeader} />
              </div>
            </div>
          </div>

          {/* Scope */}
          <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', background:'rgba(233,112,36,0.04)', border:'1px solid rgba(233,112,36,0.15)', borderRadius:8, padding:'0.625rem 0.875rem', fontSize:'0.78rem', color:'#666' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#e97024" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            Exporting <strong style={{ color:'#333', margin:'0 3px' }}>{count} requirement{count !== 1 ? 's' : ''}</strong> matching your active filters.
          </div>

        </div>

        {/* Footer */}
        <div style={{ padding:'1rem 1.5rem', borderTop:'1px solid #f0f0f0', display:'flex', alignItems:'center', justifyContent:'space-between', background:'#fafafa', borderRadius:'0 0 12px 12px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'0.375rem' }}>
            <div style={{ width:16, height:16, background:'#e97024', borderRadius:3, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><rect x="1" y="1" width="3.5" height="3.5" rx="0.6" fill="white"/><rect x="5.5" y="1" width="3.5" height="3.5" rx="0.6" fill="white"/><rect x="1" y="5.5" width="3.5" height="3.5" rx="0.6" fill="white"/><rect x="5.5" y="5.5" width="3.5" height="3.5" rx="0.6" fill="white"/></svg>
            </div>
            <span style={{ fontSize:'0.72rem', fontWeight:700, color:'#333' }}>CODiii</span>
            <span style={{ fontSize:'0.68rem', color:'#bbb' }}>by OQULi</span>
          </div>
          <div style={{ display:'flex', gap:'0.625rem' }}>
            <button onClick={onClose} style={{ padding:'0.5rem 1rem', background:'transparent', color:'#666', border:'1px solid #e0e0e0', borderRadius:6, fontSize:'0.82rem', fontWeight:500, fontFamily:'inherit', cursor:'pointer' }}>Cancel</button>
            <button
              onClick={handleExport}
              disabled={!hasAnyCol}
              style={{ display:'inline-flex', alignItems:'center', gap:'0.4rem', padding:'0.5rem 1.125rem', background: hasAnyCol ? '#e97024' : '#e0e0e0', color: hasAnyCol ? '#fff' : '#aaa', border:'none', borderRadius:6, fontSize:'0.82rem', fontWeight:600, fontFamily:'inherit', cursor: hasAnyCol ? 'pointer' : 'not-allowed' }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
              Export {FORMATS.find(f => f.key === selectedFormat)?.label}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

function FormatIcon({ type }) {
  const styles = {
    csv:  { background:'rgba(56,142,60,0.1)',  color:'#388e3c' },
    xlsx: { background:'rgba(56,142,60,0.12)', color:'#2e7d32' },
    html: { background:'rgba(37,99,235,0.1)',  color:'#2563eb' },
    pdf:  { background:'rgba(220,38,38,0.1)',  color:'#dc2626' },
  };
  return (
    <div style={{ width:36, height:36, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', ...styles[type] }}>
      {type === 'csv' && <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/></svg>}
      {type === 'xlsx' && <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M3 15h18M9 3v18"/></svg>}
      {type === 'html' && <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>}
      {type === 'pdf' && <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>}
    </div>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <div onClick={() => onChange(!checked)} style={{ position:'relative', width:34, height:20, flexShrink:0, cursor:'pointer' }}>
      <div style={{ position:'absolute', inset:0, background: checked ? '#e97024' : '#e0e0e0', borderRadius:10, transition:'background 0.2s' }} />
      <div style={{ position:'absolute', top:3, left: checked ? 17 : 3, width:14, height:14, background:'#fff', borderRadius:'50%', boxShadow:'0 1px 3px rgba(0,0,0,0.2)', transition:'left 0.2s' }} />
    </div>
  );
}